import Link from "next/link";

/**
 * Aditya Labs brand v2 — exact PNG lockup component.
 *
 * Renders the canonical screenshot-derived PNG assets for the active
 * website surfaces. The SVGs remain archived/reference only; runtime
 * surfaces use the exact PNG lockups so the approved screenshot is the
 * visual source of truth.
 *
 * Honours prefers-reduced-motion by relying on a static asset — no
 * animations are introduced by this component.
 */
interface BrandLogoProps {
  /** Pixel height of the lockup. Defaults to 32 (header). */
  size?: number;
  /** Additional class names on the outer wrapper. */
  className?: string;
  /** When true, renders the full horizontal lockup with the wordmark. */
  showName?: boolean;
  /** Optional explicit asset family. */
  asset?: "header" | "footer" | "symbol";
  /** Wraps the lockup in a Link to the homepage when true (default). */
  linked?: boolean;
}

export default function BrandLogo({
  size = 32,
  className = "",
  showName = false,
  asset,
  linked = true,
}: BrandLogoProps) {
  const assetKind = asset ?? (showName ? "header" : "symbol");
  const src =
    assetKind === "header"
      ? "/brand/exact/aditya-labs-final-header-lockup.png"
      : assetKind === "footer"
        ? "/brand/exact/aditya-labs-final-footer-lockup.png"
        : "/brand/exact/aditya-labs-final-symbol-favicon-appicon.png";

  const content = (
    <img
      src={src}
      alt={showName ? "Aditya Labs" : "Aditya Labs primary symbol"}
      width={assetKind === "symbol" ? size : undefined}
      height={size}
      loading="eager"
      decoding="async"
      className={`block shrink-0 object-contain ${className}`}
      style={{ height: `${size}px`, width: assetKind === "symbol" ? `${size}px` : "auto" }}
    />
  );

  if (linked) {
    return (
      <Link
        href="/"
        aria-label="Aditya Labs — AI agents for small business"
        className="inline-flex items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B1226]"
      >
        {content}
      </Link>
    );
  }

  return content;
}
